import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Plus, Search, Edit, Trash2, Eye, Star,
    Calendar, MapPin, Tag, CheckCircle, XCircle
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { newsApi } from '../../lib/api'

interface Article {
    id: number
    slug: string
    title_en?: string
    title_vi?: string
    category: string
    status: string
    featured: number
    views: number
    created_at: string
    location?: string
}

export default function NewsManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('all')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
    const [articles, setArticles] = useState<Article[]>([])
    const [_loading, setLoading] = useState(true)
    const [categories, setCategories] = useState<{ id: string; slug: string; name: { en: string; vi: string } }[]>([
        { id: 'all', slug: 'all', name: { en: 'All', vi: 'Tất cả' } }
    ])

    // Fetch categories from DB
    useEffect(() => {
        fetch('/api/categories')
            .then(r => r.json())
            .then(data => {
                if (data.categories) {
                    const dbCats = data.categories.map((cat: any) => ({
                        id: cat.slug,
                        slug: cat.slug,
                        name: { en: cat.name_en, vi: cat.name_vi }
                    }))
                    setCategories([{ id: 'all', slug: 'all', name: { en: 'All', vi: 'Tất cả' } }, ...dbCats])
                }
            })
            .catch(err => console.error('Failed to load categories:', err))
    }, [])

    useEffect(() => {
        const fetchArticles = async () => {
            setLoading(true)
            try {
                const params = selectedCategory !== 'all' ? { category: selectedCategory } : undefined
                const response = await newsApi.list(params)
                if (response.data?.data) {
                    setArticles(response.data.data)
                }
            } catch (error) {
                console.error('Failed to fetch articles:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchArticles()
    }, [selectedCategory])

    const filteredArticles = articles.filter((article) => {
        const title = (language === 'en' ? article.title_en : (article.title_vi || article.title_en)) || ''
        return title.toLowerCase().includes(searchQuery.toLowerCase())
    })

    const handleDelete = async () => {
        if (!selectedArticle) return
        try {
            await newsApi.delete(selectedArticle.id)
            setArticles(prev => prev.filter(a => a.id !== selectedArticle.id))
            setShowDeleteModal(false)
            setSelectedArticle(null)
            alert(language === 'en' ? 'Article deleted!' : 'Đã xóa bài viết!')
        } catch (error) {
            console.error('Failed to delete:', error)
            alert(language === 'en' ? 'Failed to delete' : 'Lỗi khi xóa')
        }
    }

    const handleTogglePublish = async (article: Article) => {
        try {
            if (article.status === 'published') {
                await newsApi.unpublish(article.id)
                setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'draft' } : a))
            } else {
                await newsApi.publish(article.id)
                setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'published' } : a))
            }
        } catch (error) {
            console.error('Failed to toggle publish:', error)
            alert(language === 'en' ? 'Failed to update status' : 'Lỗi cập nhật trạng thái')
        }
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'Blog Management' : 'Quản Lý Blog'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Create and manage blog posts' : 'Tạo và quản lý bài viết blog'}
                    </p>
                </div>
                <Link
                    to="/admin/blog/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                    <Plus className="w-5 h-5" />
                    {language === 'en' ? 'New Article' : 'Bài viết mới'}
                </Link>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm border">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            placeholder={language === 'en' ? 'Search articles...' : 'Tìm bài viết...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="flex gap-2 overflow-x-auto">
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${selectedCategory === cat.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {cat.name[language]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Articles Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'en' ? 'Article' : 'Bài viết'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    {language === 'en' ? 'Category' : 'Danh mục'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                    {language === 'en' ? 'Status' : 'Trạng thái'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                                    {language === 'en' ? 'Views' : 'Lượt xem'}
                                </th>
                                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                                    {language === 'en' ? 'Date' : 'Ngày'}
                                </th>
                                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {language === 'en' ? 'Actions' : 'Thao tác'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredArticles.map((article) => (
                                <tr key={article.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <Tag className="w-5 h-5 text-gray-400" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 line-clamp-1 flex items-center gap-2">
                                                    {language === 'en' ? article.title_en : (article.title_vi || article.title_en) || 'Untitled'}
                                                    {article.featured === 1 && (
                                                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                                    )}
                                                </p>
                                                <p className="text-sm text-gray-500 flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {article.location}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden md:table-cell">
                                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                                            {categories.find((c) => c.id === article.category)?.name[language]}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <span className={`px-2 py-1 rounded text-sm font-medium ${article.status === 'published'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                            {article.status === 'published'
                                                ? (language === 'en' ? 'Published' : 'Đã xuất bản')
                                                : (language === 'en' ? 'Draft' : 'Nháp')
                                            }
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell text-gray-600">
                                        {article.views.toLocaleString()}
                                    </td>
                                    <td className="px-6 py-4 hidden md:table-cell text-gray-600 text-sm">
                                        <div className="flex items-center gap-1">
                                            <Calendar className="w-4 h-4" />
                                            {article.created_at?.slice(0, 10) || '-'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            <Link
                                                to={`/blog/${article.category}/${article.slug}`}
                                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                                title={language === 'en' ? 'View' : 'Xem'}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Link>
                                            <Link
                                                to={`/admin/blog/${article.id}/edit`}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                title={language === 'en' ? 'Edit' : 'Sửa'}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Link>
                                            {/* Publish/Unpublish Toggle */}
                                            <button
                                                onClick={() => handleTogglePublish(article)}
                                                className={`p-2 rounded-lg ${article.status === 'published'
                                                    ? 'text-yellow-600 hover:bg-yellow-50'
                                                    : 'text-green-600 hover:bg-green-50'}`}
                                                title={article.status === 'published'
                                                    ? (language === 'en' ? 'Unpublish' : 'Bỏ xuất bản')
                                                    : (language === 'en' ? 'Publish' : 'Xuất bản')}
                                            >
                                                {article.status === 'published' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSelectedArticle(article)
                                                    setShowDeleteModal(true)
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                title={language === 'en' ? 'Delete' : 'Xóa'}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredArticles.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        {language === 'en' ? 'No articles found' : 'Không tìm thấy bài viết'}
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-2">
                            {language === 'en' ? 'Delete Article' : 'Xóa Bài Viết'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {language === 'en'
                                ? `Are you sure you want to delete "${language === 'en' ? selectedArticle?.title_en : selectedArticle?.title_vi}"?`
                                : `Bạn có chắc muốn xóa "${selectedArticle?.title_vi || selectedArticle?.title_en}"?`
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                {language === 'en' ? 'Delete' : 'Xóa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
